import { Injectable, signal } from '@angular/core';
import { FilterOption, FilterOptionsResponse, Filters, MainFilterOption } from './models';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, finalize, switchMap, tap } from 'rxjs';

export interface FilterPaginationState {
  currentPage: number;
  hasMore: boolean;
  allOptions: FilterOption[];
  isLoading: boolean;
}

@Injectable()
export class FilterService {
  public localFilterOptions = signal<MainFilterOption[]>([]);
  public paginationStates = signal<Record<string, FilterPaginationState>>({});
  public searchControls = signal<Record<string, FormControl>>({});
  public hasMoreOptions = signal<Record<string, boolean>>({});

  public initialize(filterOptions: MainFilterOption[]) {
    const controls: Record<string, FormControl> = {};
    const paginationStates: Record<string, FilterPaginationState> = {};
    const initialHasMore: Record<string, boolean> = {};

    filterOptions.forEach((option) => {
      if (option.optionsObservable) {
        const control = new FormControl('');
        controls[option.key] = control;

        paginationStates[option.key] = {
          currentPage: 0,
          hasMore: option.hasMore ?? false,
          allOptions: option.options || [],
          isLoading: false,
        };
        initialHasMore[option.key] = paginationStates[option.key].hasMore;

        control.valueChanges
          .pipe(
            debounceTime(300),
            distinctUntilChanged(),
            tap(() => this.setLoading(option.key, true, true)),
            switchMap((searchTerm: string) =>
              option.optionsObservable!(searchTerm, 0).pipe(finalize(() => this.setLoading(option.key, false))),
            ),
          )
          .subscribe((response: FilterOptionsResponse) => {
            this.updateStateAfterLoad(option.key, response, false);
          });
      }
    });

    this.localFilterOptions.set(filterOptions);
    this.searchControls.set(controls);
    this.paginationStates.set(paginationStates);
    this.hasMoreOptions.set(initialHasMore);
  }

  public loadMoreOptions(option: MainFilterOption): void {
    const state = this.paginationStates()[option.key];
    const searchTerm = this.searchControls()[option.key]?.value || '';

    if (!option.optionsObservable || state.isLoading || !state.hasMore) {
      return;
    }

    const nextPage = state.currentPage + 1;
    this.setLoading(option.key, true);

    option
      .optionsObservable(searchTerm, nextPage)
      .pipe(finalize(() => this.setLoading(option.key, false)))
      .subscribe((response) => {
        this.updateStateAfterLoad(option.key, response, true, nextPage);
      });
  }

  public toggleFilter(filters: Filters, key: string, value: string | boolean): Filters | undefined {
    if (!key) {
      return undefined;
    }
    const selected = filters[key] || [];
    if (typeof value === 'boolean') {
      if (value) {
        filters[key] = ['true'];
      } else {
        delete filters[key];
      }
    } else {
      const filterArray = selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value];

      if (filterArray.length > 0) {
        filters[key] = filterArray;
      } else {
        delete filters[key];
      }
    }
    return filters;
  }

  private setLoading(key: string, isLoading: boolean, isReset = false) {
    this.paginationStates.update((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        isLoading,
        ...(isReset ? { allOptions: [], currentPage: 0, hasMore: true } : {}),
      },
    }));
  }

  private updateStateAfterLoad(key: string, response: FilterOptionsResponse, isAppend: boolean, page = 0) {
    this.paginationStates.update((prev) => {
      const newOptions = isAppend ? [...prev[key].allOptions, ...response.items] : response.items;
      return {
        ...prev,
        [key]: { ...prev[key], allOptions: newOptions, hasMore: response.hasMore, currentPage: page },
      };
    });

    this.localFilterOptions.update((options) =>
      options.map((opt) => (opt.key === key ? { ...opt, options: this.paginationStates()[key].allOptions } : opt)),
    );

    this.hasMoreOptions.update((prev) => ({ ...prev, [key]: response.hasMore }));
  }
}
